import React, { useEffect, useState, useCallback } from 'react';
import { Select, SelectProps } from '@amzn/awsui-components-console';

export const FONT_PREFERENCE_KEY = 'snow_font_preference';

export enum FontOption {
	DEFAULT = 'default',
	COMIC_SANS = 'comic-sans',
	GEORGIA = 'georgia',
	VERDANA = 'verdana',
	COURIER = 'courier',
}

interface FontConfig {
	label: string;
	fontFamily: string;
}

export const FONT_CONFIG: Record<FontOption, FontConfig> = {
	[FontOption.DEFAULT]: {
		label: 'Default (Amazon Ember)',
		fontFamily: '"Amazon Ember", "Helvetica Neue", Roboto, Arial, sans-serif',
	},
	[FontOption.COMIC_SANS]: {
		label: 'Comic Sans',
		fontFamily: '"Comic Sans MS", "Comic Sans", cursive',
	},
	[FontOption.GEORGIA]: {
		label: 'Georgia',
		fontFamily: 'Georgia, "Times New Roman", Times, serif',
	},
	[FontOption.VERDANA]: {
		label: 'Verdana',
		fontFamily: 'Verdana, Geneva, sans-serif',
	},
	[FontOption.COURIER]: {
		label: 'Courier',
		fontFamily: '"Courier New", Courier, monospace',
	},
};

const FONT_OPTIONS: SelectProps.Option[] = Object.entries(FONT_CONFIG).map(([value, config]) => ({
	value,
	label: config.label,
	description: config.fontFamily.split(',')[0].replace(/"/g, ''),
}));

export const applyFont = (font: FontOption): void => {
	const fontFamily = FONT_CONFIG[font]?.fontFamily || FONT_CONFIG[FontOption.DEFAULT].fontFamily;

	document.documentElement.style.setProperty('--app-font-family', fontFamily);
	
	let styleEl = document.getElementById('snow-font-override');

	if (!styleEl) {
		styleEl = document.createElement('style');
		styleEl.id = 'snow-font-override';
		document.head.appendChild(styleEl);
	}
	
	styleEl.textContent = `
		*, *::before, *::after {
			font-family: ${fontFamily} !important;
		}
	`;
};

export const getStoredFont = (): FontOption => {
	const stored = localStorage.getItem(FONT_PREFERENCE_KEY);

	return (stored as FontOption) || FontOption.DEFAULT;
};

export const useFontPreference = () => {
	const [font, setFont] = useState<FontOption>(getStoredFont);

	useEffect(() => {
		applyFont(font);
	}, [font]);

	const changeFont = useCallback((newFont: FontOption) => {
		setFont(newFont);
		localStorage.setItem(FONT_PREFERENCE_KEY, newFont);
		applyFont(newFont);
	}, []);

	return { font, changeFont };
};

interface FontPreferenceProps {
	compact?: boolean;
}

const FontPreference: React.FC<FontPreferenceProps> = ({ compact = false }) => {
	const { font, changeFont } = useFontPreference();

	const handleChange: SelectProps['onChange'] = ({ detail }) => {
		changeFont(detail.selectedOption.value as FontOption);
	};

	const fontOptions: SelectProps.Option[] = Object.entries(FONT_CONFIG).map(([value, config]) => ({
		value,
		label: config.label,
	}));

	return (
		<Select
			selectedOption={fontOptions.find(opt => opt.value === font) || fontOptions[0]}
			onChange={handleChange}
			options={fontOptions}
			placeholder="Select font"
			expandToViewport
			triggerVariant={compact ? 'option' : undefined}
		/>
	);
};

export default FontPreference;
